import React, { useMemo } from 'react';
import closeSvg from 'assets/icons/close.svg';
import personSvg from 'assets/icons/person.svg';
import Button, { Color as ButtonColor } from 'lib/Button';
import Chip, { Color } from 'lib/Chip';
import CopyToClipboardButton from 'components/common/CopyToClipboardButton';
import { FurnitureItem } from 'components/pages/Planner/PlannerMap/useFurniture.types';
import { CardPosition } from 'components/pages/Planner/PlannerMap/PlannerMap.types';
import CampaignPlanSingleFurnitureCardItem from '../CampaignPlanSingleFurnitureCard/CampaignPlanSingleFurnitureCardItem';
import CampaignPlanMultiFrameCard from './CampaignPlanMultiFrameCard';

export interface CampaignPlanMultiFurnitureCardProps {
  furnitures: FurnitureItem[];
  cardPosition: CardPosition;
  onClose: VoidFunction;
  openStreetView: ({ lat, lng }: { lat: number; lng: number }) => void;
}

const CampaignPlanMultiFurnitureCard = ({
  furnitures,
  cardPosition,
  onClose,
  openStreetView,
}: CampaignPlanMultiFurnitureCardProps): React.ReactElement => {
  const { position } = cardPosition;

  const totalFrames = useMemo(() => furnitures.reduce((sum, f) => sum + f.assetsInformation.length, 0), [furnitures]);

  const streetViewCentre = useMemo(() => {
    if (!furnitures.length) return null;
    const lat = furnitures.reduce((sum, f) => sum + f.latitude, 0) / furnitures.length;
    const lng = furnitures.reduce((sum, f) => sum + f.longitude, 0) / furnitures.length;

    return { lat, lng };
  }, [furnitures]);

  return (
    <div
      data-test-id="campaign-plan-multi-furniture-card"
      className="absolute rounded-xl shadow-lg bg-white w-[330px] overscroll-contain flex flex-col max-h-[80%] min-h-0"
      style={{
        left: `${position[0]}px`,
        top: `${position[1]}px`,
        transform: 'translate(-113%, -40%)',
      }}
    >
      <div
        data-test-id="campaign-plan-multi-furniture-card-header"
        className="w-full flex items-center rounded-t-xl justify-between mb-2 py-5 px-4 bg-neutral-200"
      >
        <div className="w-full flex items-center space-x-2">
          <Chip
            dataTestId="campaign-plan-multi-furniture-card-total-frames"
            label={totalFrames.toString()}
            color={Color.DARK_GREY}
          />
        </div>
        <div className="flex items-center space-x-1">
          {streetViewCentre && (
            <Button
              dataTestId="campaign-plan-multi-furniture-card-open-street-view"
              svg={personSvg}
              color={ButtonColor.GREY}
              svgClassnames="w-4 h-4 text-neutral-900"
              onClick={() => openStreetView(streetViewCentre)}
            />
          )}
          <Button
            dataTestId="campaign-plan-multi-furniture-card-close"
            svg={closeSvg}
            color={ButtonColor.GREY}
            svgClassnames="w-4 h-4 text-neutral-900"
            onClick={onClose}
          />
        </div>
      </div>

      {furnitures[0]?.address1 && (
        <div className="flex items-center px-4 pb-2 group">
          <span className="flex-1 body-base text-gray-800 whitespace-nowrap overflow-hidden truncate">
            {furnitures[0].address1}
          </span>
          <CopyToClipboardButton
            dataTestId="campaign-plan-multi-furniture-card-copy-address"
            textToCopy={furnitures[0].address1}
            color={ButtonColor.TERTIARY}
            svgClassnames="w-3 h-3 text-neutral-900"
            buttonClassnames="!p-1 opacity-0 group-hover:opacity-100"
            isDisabled={false}
          />
        </div>
      )}

      <div className="space-y-3 flex flex-col overflow-y-auto px-4 pb-3 min-h-0">
        {furnitures.map((furniture) => {
          if (furniture.assetsInformation.length > 1) {
            return (
              <CampaignPlanMultiFrameCard
                key={furniture.furnitureCode}
                furnitureCode={furniture.furnitureCode}
                assets={furniture.assetsInformation}
              />
            );
          }

          return (
            <CampaignPlanSingleFurnitureCardItem
              key={furniture.furnitureCode}
              frameId={furniture.assetsInformation[0].frameId}
              isDigital={furniture.assetsInformation[0].isDigital}
              frameSize={furniture.assetsInformation[0].frameSize}
              impressions={furniture.assetsInformation[0].impressions}
              isImpressions={furniture.assetsInformation[0].isImpressions ?? false}
              sot={furniture.assetsInformation[0].sot}
            />
          );
        })}
      </div>
    </div>
  );
};

export default CampaignPlanMultiFurnitureCard;
